import React, { useState } from 'react';
import QRCode from 'react-qr-code';

/**
 * Reusable "share your workspace login link" card.
 *
 * Surfaces the full https://.../login/<slug> URL with:
 *   • Click-to-copy button (clipboard + 2 s success toast)
 *   • Inline QR code for scanning from a mobile camera
 *   • "Open in new tab" link
 *   • Disabled "Email invite" button (Phase C — coming soon)
 *
 * Props:
 *   slug:        organization slug (required)
 *   orgName:     human-readable org name (used in copy / accessible labels)
 *   compact:     if true, render a tighter layout (used inside Organization tab)
 */
export function WorkspaceLinkCard({ slug, orgName = 'your provider', compact = false }) {
  const [copied, setCopied] = useState(false);
  const [qrOpen, setQrOpen] = useState(!compact);

  if (!slug) return null;

  // Build the absolute URL using the current browser origin so previews,
  // staging, and production each get the right host without hardcoding.
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const loginUrl = `${origin}/login/${slug}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(loginUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for very old browsers / restricted contexts
      const ta = document.createElement('textarea');
      ta.value = loginUrl;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); setCopied(true); setTimeout(() => setCopied(false), 2000); }
      catch { /* swallow */ }
      document.body.removeChild(ta);
    }
  };

  return (
    <div
      className="card"
      style={{
        marginBottom: '1.5rem',
        background: 'linear-gradient(135deg, hsl(var(--primary) / 0.08) 0%, hsl(var(--secondary) / 0.08) 100%)',
        borderColor: 'hsl(var(--primary) / 0.2)',
      }}
      data-testid="workspace-link-card"
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div>
          <h3 style={{ margin: '0 0 0.25rem 0', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            🔗 Your workspace login link
          </h3>
          <p style={{ margin: 0, fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))' }}>
            Share this URL with parents and staff so they can sign in to {orgName} on a branded login page.
          </p>
        </div>
      </div>

      {/* URL row */}
      <div
        style={{
          marginTop: '1rem',
          display: 'flex',
          gap: '0.5rem',
          alignItems: 'stretch',
          flexWrap: 'wrap',
        }}
      >
        <input
          readOnly
          value={loginUrl}
          onFocus={(e) => e.target.select()}
          aria-label="Workspace login URL"
          data-testid="workspace-login-url"
          style={{
            flex: 1,
            minWidth: '260px',
            fontFamily: 'monospace',
            fontSize: '0.9rem',
            padding: '0.6rem 0.75rem',
            border: '1px solid hsl(var(--border))',
            borderRadius: 'var(--radius)',
            background: '#fff',
          }}
        />
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleCopy}
          data-testid="workspace-copy-btn"
          style={{ minWidth: '110px' }}
        >
          {copied ? '✅ Copied!' : '📋 Copy link'}
        </button>
        <a
          href={loginUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-ghost"
          data-testid="workspace-open-link"
          style={{ display: 'inline-flex', alignItems: 'center', textDecoration: 'none' }}
        >
          ↗ Open
        </a>
      </div>

      {/* QR + invite row */}
      <div style={{ marginTop: '1rem', display: 'flex', flexWrap: 'wrap', gap: '1rem', alignItems: 'flex-start' }}>
        <button
          type="button"
          onClick={() => setQrOpen(o => !o)}
          className="btn btn-ghost"
          data-testid="workspace-qr-toggle"
          style={{ alignSelf: 'flex-start' }}
        >
          {qrOpen ? '▾ Hide QR code' : '▸ Show QR code'}
        </button>

        <button
          type="button"
          onClick={() => {
            // Scroll/focus the InviteUsersCard rendered immediately below in
            // the Organization tab. Falls back to no-op if it isn't mounted
            // (e.g. used inside WorkspaceReadyModal, where there is no card
            // to jump to yet).
            const target = typeof document !== 'undefined'
              ? document.getElementById('invite-users-card')
              : null;
            if (target) {
              target.scrollIntoView({ behavior: 'smooth', block: 'start' });
              // Move focus to the first form field for keyboard users.
              const emailInput = target.querySelector('[data-testid="invite-email-input"]');
              if (emailInput) emailInput.focus({ preventScroll: true });
            }
          }}
          data-testid="workspace-email-invite-btn"
          style={{
            padding: '0.5rem 0.85rem',
            border: '1px solid hsl(var(--primary) / 0.4)',
            borderRadius: 'var(--radius)',
            background: 'hsl(var(--primary) / 0.08)',
            cursor: 'pointer',
            color: 'hsl(var(--primary))',
            fontSize: '0.85rem',
            fontWeight: 500,
          }}
        >
          ✉️ Invite users by email
        </button>
      </div>

      {qrOpen && (
        <div
          style={{
            marginTop: '1rem',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '1rem',
            background: '#fff',
            borderRadius: 'var(--radius)',
            border: '1px solid hsl(var(--border))',
            maxWidth: '260px',
          }}
          data-testid="workspace-qr-container"
        >
          <QRCode
            value={loginUrl}
            size={180}
            level="M"
            bgColor="#ffffff"
            fgColor="#000000"
          />
          <small style={{ color: 'hsl(var(--muted-foreground))', textAlign: 'center' }}>
            Scan with a phone camera to open the branded login page
          </small>
        </div>
      )}
    </div>
  );
}

export default WorkspaceLinkCard;
